<template>
  <div class="music-player" :class="{ 'music-loading': isTransitioning }">
    <button class="music-control" @click="$emit('togglePlay')">
      <svg v-if="isPlaying" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="6" y="4" width="4" height="16" fill="currentColor"/>
        <rect x="14" y="4" width="4" height="16" fill="currentColor"/>
      </svg>
      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <polygon points="5,3 19,12 5,21" fill="currentColor"/>
      </svg>
    </button>
    
    <div class="music-info">
      <div class="music-title">{{ currentTrack.title }}</div>
      <div class="music-artist">{{ currentTrack.artist }}</div>
      <div class="music-progress">
        <div class="music-progress-bar" :style="{ width: progressPercentage + '%' }"></div>
      </div>
    </div>
    
    <button class="music-control playlist-button" @click="$emit('toggle-playlist')" title="播放列表">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <!-- 音乐列表图标 -->
        <rect x="3" y="5" width="12" height="2" rx="1" fill="currentColor" opacity="0.9"/>
        <rect x="3" y="9" width="10" height="2" rx="1" fill="currentColor" opacity="0.8"/>
        <rect x="3" y="13" width="11" height="2" rx="1" fill="currentColor" opacity="0.8"/>
        <rect x="3" y="17" width="9" height="2" rx="1" fill="currentColor" opacity="0.7"/>
        
        <!-- 音乐符号装饰 -->
        <circle cx="18" cy="8" r="2.5" stroke="currentColor" stroke-width="1.5" fill="none" opacity="0.8"/>
        <polygon points="17,6.5 17,9.5 19.5,8" fill="currentColor" opacity="0.8"/>
        
        <!-- 音符装饰 -->
        <path d="M20 12v6" stroke="currentColor" stroke-width="1.5" opacity="0.6"/>
        <circle cx="20" cy="18" r="1.5" fill="currentColor" opacity="0.6"/>
        <path d="M20 12l2-0.5v3l-2 0.5" fill="currentColor" opacity="0.6"/>
      </svg>
    </button>
    
    <button class="music-control" @click="$emit('nextTrack')" title="下一首">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <polygon points="5,4 15,12 5,20" fill="currentColor"/>
        <line x1="19" y1="5" x2="19" y2="19" stroke="currentColor" stroke-width="2"/>
      </svg>
    </button>
    
  </div>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'MusicPlayer',
  props: {
    currentTrack: {
      type: Object,
      required: true
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    isMuted: {
      type: Boolean,
      default: false
    },
    volume: {
      type: Number,
      default: 30
    },
    currentTime: {
      type: Number,
      default: 0
    },
    duration: {
      type: Number,
      default: 0
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isTransitioning: {
      type: Boolean,
      default: false
    }
  },
  emits: ['togglePlay', 'nextTrack', 'toggle-playlist'],
  setup(props) {
    const progressPercentage = computed(() => {
      if (props.duration === 0) return 0
      return (props.currentTime / props.duration) * 100
    })
    
    
    return {
      progressPercentage
    }
  }
}
</script>

<style scoped>
.music-player {
  position: fixed;
  bottom: 30px;
  left: 30px;
  display: flex;
  align-items: center;
  gap: 15px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  padding: 12px 20px;
  z-index: 100;
  transition: all 0.3s ease;
  max-width: 400px;
  min-width: 320px;
}

.music-control {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
}

.music-control:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
  transform: scale(1.1);
}

.music-control svg {
  width: 20px;
  height: 20px;
  fill: currentColor;
  stroke: currentColor;
}

/* iOS Safari按钮显示修复 - 边框+图标样式 */
@supports (-webkit-touch-callout: none) {
  .music-control {
    background: transparent !important;
    border: 2px solid rgba(255, 255, 255, 0.6) !important;
    backdrop-filter: none !important;
  }
  
  .music-control:hover {
    background: transparent !important;
    border-color: rgba(255, 255, 255, 0.9) !important;
  }
  
  .music-control svg {
    fill: none !important;
    stroke: rgba(255, 255, 255, 0.8) !important;
    stroke-width: 1.5 !important;
  }
  
  .music-control:hover svg {
    stroke: rgba(255, 255, 255, 1) !important;
  }
  
  .music-control svg path,
  .music-control svg polygon,
  .music-control svg rect,
  .music-control svg circle {
    fill: rgba(255, 255, 255, 0.8) !important;
    stroke: none !important;
  }
  
  .music-control:hover svg path,
  .music-control:hover svg polygon,
  .music-control:hover svg rect,
  .music-control:hover svg circle {
    fill: rgba(255, 255, 255, 1) !important;
  }
}

.playlist-button {
  position: relative;
  overflow: hidden;
}

.playlist-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.5s ease;
}

.playlist-button:hover::before {
  left: 100%;
}

.playlist-button:hover svg {
  transform: scale(1.05);
  transition: transform 0.3s ease;
}

.playlist-button svg {
  transition: transform 0.3s ease;
}

.music-info {
  flex: 1;
  min-width: 120px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
}

.music-title {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.music-artist {
  font-size: 0.75rem;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}


.music-loading {
  opacity: 0.6;
}

.music-loading .music-control {
  pointer-events: none;
}

.music-progress {
  width: 100%;
  height: 2px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 1px;
  margin-top: 8px;
  position: relative;
}

.music-progress-bar {
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 1px;
  width: 0%;
  transition: width 0.3s ease;
}

/* 移动端响应式 */
@media (max-width: 768px) {
  .music-player {
    bottom: 20px;
    left: 15px;
    right: 15px;
    max-width: none;
    padding: 10px 16px;
    border-radius: 20px;
  }

  .music-info {
    font-size: 0.8rem;
  }

}
</style>